import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  ArrowLeft, Loader2, Tags, Plus, Pencil, Trash2, ChevronUp, ChevronDown,
  StickyNote, BookmarkPlus, Layers, PoundSterling,
} from "lucide-react";
import { quotesApi, pricingApi, jobTemplatesApi } from "@/services/api";
import { fmtMoney, apiError } from "@/lib/format";
import { LoadingState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const stripPrefix = (s) =>
  String(s || "").replace(/^\s*⚠?\s*Unmatched\s*[—-]\s*/i, "").replace(/^Repair:\s*/i, "").trim();
const lineTotal = (it) => (Number(it.cost) || 0) * (Number(it.quantity) || 1);

function groupByDoor(quote) {
  const items = quote?.items || [];
  const map = new Map();
  const nonDoor = [];
  items.forEach((it, idx) => {
    const withIdx = { ...it, _idx: idx };
    const key = String(it.door_reference || it.door_id || "");
    const doored = !it.extra_work && key;
    if (doored) {
      if (!map.has(key)) {
        map.set(key, { key, ref: it.door_reference || key, loc: it.door_location || "", door_id: it.door_id || null, door_type: it.door_type || null, items: [], total: 0 });
      }
      const slot = map.get(key);
      if (!slot.loc && it.door_location) slot.loc = it.door_location;
      slot.items.push(withIdx);
      slot.total += lineTotal(it);
    } else {
      nonDoor.push(withIdx);
    }
  });
  const doorOrder = Array.isArray(quote?.door_order) ? quote.door_order.map(String) : [];
  const doors = Array.from(map.values()).sort((a, b) => {
    if (doorOrder.length) {
      const ia = doorOrder.indexOf(String(a.key));
      const ib = doorOrder.indexOf(String(b.key));
      const aa = ia === -1 ? 9999 : ia;
      const bb = ib === -1 ? 9999 : ib;
      if (aa !== bb) return aa - bb;
    }
    const sa = a.ref.replace(/\d+/g, ""), sb = b.ref.replace(/\d+/g, "");
    if (sa === sb) {
      const na = parseInt(a.ref.replace(/\D+/g, ""), 10);
      const nb = parseInt(b.ref.replace(/\D+/g, ""), 10);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
    }
    return a.ref.localeCompare(b.ref);
  });
  return { doors, nonDoor };
}

export default function QuoteFullEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [quote, setQuote] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Single-line edit
  const [editIdx, setEditIdx] = useState(null);
  const [eDesc, setEDesc] = useState("");
  const [eCost, setECost] = useState("");
  const [eQty, setEQty] = useState("1");
  const [eSaveLib, setESaveLib] = useState(false);

  // Add job to door
  const [addDoorKey, setAddDoorKey] = useState(null);
  const [aName, setAName] = useState("");
  const [aCost, setACost] = useState("");
  const [aQty, setAQty] = useState("1");

  // Bulk price
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkVals, setBulkVals] = useState({});
  const [bulkSaveLib, setBulkSaveLib] = useState(true);

  // Per-door note
  const [noteDoorKey, setNoteDoorKey] = useState(null);
  const [noteText, setNoteText] = useState("");

  // Discount
  const [discOpen, setDiscOpen] = useState(false);
  const [discAmount, setDiscAmount] = useState("");
  const [discLabel, setDiscLabel] = useState("");
  const [discMode, setDiscMode] = useState("fixed");

  // Templates
  const [applyDoorKey, setApplyDoorKey] = useState(null);
  const [saveTplDoorKey, setSaveTplDoorKey] = useState(null);
  const [tplName, setTplName] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await quotesApi.getOne(id);
      setQuote(r.data);
      try { const t = await jobTemplatesApi.list(); setTemplates(t.data || []); } catch { /* non-fatal */ }
    } catch (e) {
      toast.error(apiError(e, "Could not load quote"));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const items = quote?.items || [];
  const isLocked = quote && (quote.status === "accepted" || quote.status === "declined");
  const unpricedIdxs = items.map((it, idx) => ({ it, idx })).filter(({ it }) => (Number(it.cost) || 0) <= 0);

  const openBulk = useCallback(() => {
    const seed = {};
    unpricedIdxs.forEach(({ idx }) => { seed[idx] = ""; });
    setBulkVals(seed);
    setBulkSaveLib(true);
    setBulkOpen(true);
  }, [unpricedIdxs]);

  useEffect(() => {
    if (params.get("bulk") === "1" && quote && !loading && !isLocked && unpricedIdxs.length > 0) {
      openBulk();
    }
  }, [quote, loading]);

  const persist = async (nextItems) => {
    setSaving(true);
    try {
      const r = await quotesApi.update(id, { items: nextItems });
      setQuote(r.data);
      return r.data;
    } catch (e) {
      toast.error(apiError(e, "Could not save"));
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const saveToLibrary = async (it, cost) => {
    try {
      if (it.pricing_id) {
        await pricingApi.update(it.pricing_id, { default_cost: cost });
      } else {
        const component = stripPrefix(it.component) || "Additional / Custom Items";
        const reason = stripPrefix(it.defect_type || it.component) || component;
        await pricingApi.create({
          component, reason, default_cost: cost,
          failure_key: it.failure_key || undefined,
          is_custom_reason: true, vat_applicable: true,
        });
      }
    } catch { /* best-effort */ }
  };

  // Single-line edit
  const openEdit = (idx) => {
    const it = items[idx];
    setEditIdx(idx);
    setEDesc(stripPrefix(it.description || it.component || ""));
    setECost(String(it.cost ?? ""));
    setEQty(String(it.quantity ?? "1"));
    setESaveLib((Number(it.cost) || 0) <= 0);
  };
  const commitEdit = async () => {
    if (editIdx === null) return;
    const cost = parseFloat(eCost);
    const qty = parseInt(eQty, 10) || 1;
    if (!Number.isFinite(cost) || cost < 0) { toast.error("Enter a valid price (e.g. 85.00)."); return; }
    const it = items[editIdx];
    const next = items.map((x, i) => (i === editIdx ? { ...x, description: eDesc.trim() || x.component, cost, quantity: qty < 1 ? 1 : qty } : x));
    await persist(next);
    if (eSaveLib && cost > 0) await saveToLibrary(it, cost);
    setEditIdx(null);
  };
  const deleteLine = async (idx) => {
    if (!window.confirm("Remove this job from the quote?")) return;
    await persist(items.filter((_, i) => i !== idx));
  };

  // Add job to door
  const openAdd = (key) => { setAddDoorKey(key); setAName(""); setACost(""); setAQty("1"); };
  const commitAdd = async () => {
    const name = aName.trim();
    const cost = parseFloat(aCost);
    if (!name) { toast.error("Give the job a short name."); return; }
    if (!Number.isFinite(cost) || cost < 0) { toast.error("Enter a price for the job."); return; }
    const { doors } = groupByDoor(quote);
    const d = doors.find((x) => x.key === addDoorKey);
    const doorFields = d ? { door_id: d.door_id, door_reference: d.ref, door_location: d.loc || null, door_type: d.door_type } : { extra_work: true };
    await persist([...items, { component: name, description: name, cost, quantity: parseInt(aQty, 10) || 1, ...doorFields }]);
    setAddDoorKey(null);
  };

  // Bulk price
  const commitBulk = async () => {
    const next = items.map((it, idx) => {
      const v = bulkVals[idx];
      const n = v !== undefined && v !== "" ? parseFloat(v) : NaN;
      return Number.isFinite(n) && n >= 0 ? { ...it, cost: n } : it;
    });
    await persist(next);
    if (bulkSaveLib) {
      for (const { it, idx } of unpricedIdxs) {
        const v = bulkVals[idx];
        const n = v !== undefined && v !== "" ? parseFloat(v) : NaN;
        if (Number.isFinite(n) && n > 0) await saveToLibrary(it, n);
      }
    }
    setBulkOpen(false);
  };

  // Reorder within door
  const keyOf = (it) => String(it.door_reference || it.door_id || "");
  const sameDoorIdxs = (idx) => {
    const k = keyOf(items[idx]);
    return items.map((it, i) => (keyOf(it) === k ? i : -1)).filter((i) => i >= 0);
  };
  const moveWithinDoor = (idx, dir) => {
    const list = sameDoorIdxs(idx);
    const pos = list.indexOf(idx);
    const target = dir === "up" ? pos - 1 : pos + 1;
    if (target < 0 || target >= list.length) return;
    const j = list[target];
    const next = [...items];
    [next[idx], next[j]] = [next[j], next[idx]];
    persist(next);
  };
  const doorPos = (idx) => {
    const list = sameDoorIdxs(idx);
    return { pos: list.indexOf(idx), count: list.length };
  };

  // Reorder doors
  const moveDoor = async (key, dir) => {
    const order = groupByDoor(quote).doors.map((d) => String(d.key));
    const pos = order.indexOf(String(key));
    const target = dir === "up" ? pos - 1 : pos + 1;
    if (pos < 0 || target < 0 || target >= order.length) return;
    [order[pos], order[target]] = [order[target], order[pos]];
    setSaving(true);
    try { const r = await quotesApi.update(id, { door_order: order }); setQuote(r.data); }
    catch (e) { toast.error(apiError(e, "Could not reorder")); }
    finally { setSaving(false); }
  };

  // Per-door note
  const openNote = (key, ref) => {
    const notes = quote.door_notes || {};
    setNoteDoorKey(key);
    setNoteText(notes[key] || notes[ref] || "");
  };
  const saveNote = async () => {
    if (noteDoorKey === null) return;
    const next = { ...(quote.door_notes || {}) };
    const t = noteText.trim();
    if (t) next[noteDoorKey] = t; else delete next[noteDoorKey];
    setSaving(true);
    try { const r = await quotesApi.update(id, { door_notes: next }); setQuote(r.data); setNoteDoorKey(null); }
    catch (e) { toast.error(apiError(e, "Could not save note")); }
    finally { setSaving(false); }
  };

  // Discount
  const openDisc = () => {
    const pct = Number(quote.discount_percent) || 0;
    if (pct > 0) { setDiscMode("percent"); setDiscAmount(String(pct)); }
    else { setDiscMode("fixed"); setDiscAmount(quote.discount_amount ? String(quote.discount_amount) : ""); }
    setDiscLabel(quote.discount_label || "");
    setDiscOpen(true);
  };
  const saveDisc = async () => {
    const val = Math.max(0, parseFloat(discAmount) || 0);
    const label = discLabel.trim() || "Discount";
    const payload = discMode === "percent"
      ? { discount_percent: val, discount_amount: 0, discount_label: label }
      : { discount_amount: val, discount_percent: 0, discount_label: label };
    setSaving(true);
    try { const r = await quotesApi.update(id, payload); setQuote(r.data); setDiscOpen(false); }
    catch (e) { toast.error(apiError(e, "Could not save discount")); }
    finally { setSaving(false); }
  };

  // Templates
  const openSaveTpl = (key) => { setSaveTplDoorKey(key); setTplName(""); };
  const commitSaveTpl = async () => {
    const name = tplName.trim();
    if (!name) { toast.error("Give the template a name."); return; }
    const grp = groupByDoor(quote).doors.find((x) => x.key === saveTplDoorKey);
    const src = grp ? grp.items : [];
    if (!src.length) { toast.error("This door has no jobs to save."); return; }
    const jobs = src.map((j) => ({
      component: stripPrefix(j.component || j.description || "Job") || "Job",
      description: stripPrefix(j.description || j.component || ""),
      cost: Number(j.cost) || 0,
    }));
    setSaving(true);
    try {
      await jobTemplatesApi.create({ name, jobs });
      const t = await jobTemplatesApi.list();
      setTemplates(t.data || []);
      setSaveTplDoorKey(null);
      toast.success(`"${name}" saved with ${jobs.length} job${jobs.length === 1 ? "" : "s"}.`);
    } catch (e) { toast.error(apiError(e, "Could not save template")); }
    finally { setSaving(false); }
  };
  const applyTemplate = async (tpl) => {
    const grp = groupByDoor(quote).doors.find((x) => x.key === applyDoorKey);
    const doorFields = grp ? { door_id: grp.door_id, door_reference: grp.ref, door_location: grp.loc || null, door_type: grp.door_type } : {};
    const newItems = (tpl.jobs || []).map((j) => ({ component: j.component, description: j.description || j.component, cost: Number(j.cost) || 0, quantity: 1, ...doorFields }));
    setApplyDoorKey(null);
    if (newItems.length) await persist([...items, ...newItems]);
  };
  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try { await jobTemplatesApi.remove(tpl.id); setTemplates((prev) => prev.filter((t) => t.id !== tpl.id)); }
    catch (e) { toast.error(apiError(e, "Could not delete template")); }
  };

  if (loading) return <div><BackLink id={id} /><LoadingState label="Loading quote…" /></div>;
  if (!quote) return <div><BackLink id={id} /><p className="py-10 text-center text-slate-400">Quote not found.</p></div>;

  const { doors, nonDoor } = groupByDoor(quote);
  const liveSubtotal = items.reduce((s, it) => s + lineTotal(it), 0);

  const renderLine = (it) => {
    const { pos, count } = doorPos(it._idx);
    const unset = (Number(it.cost) || 0) <= 0;
    return (
      <div key={it._idx} className="flex items-start gap-3 py-2" data-testid={`fe-line-${it._idx}`}>
        <span className="mt-0.5 font-bold text-primary">•</span>
        <p className="min-w-0 flex-1 text-sm text-slate-700">{stripPrefix(it.description || it.component || "")}</p>
        {!isLocked && count > 1 && (
          <div className="flex flex-col">
            <button disabled={pos === 0} onClick={() => moveWithinDoor(it._idx, "up")} data-testid={`fe-up-${it._idx}`} className="text-slate-400 enabled:hover:text-slate-700 disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
            <button disabled={pos === count - 1} onClick={() => moveWithinDoor(it._idx, "down")} data-testid={`fe-down-${it._idx}`} className="text-slate-400 enabled:hover:text-slate-700 disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
          </div>
        )}
        {!isLocked && (
          <div className="flex items-center gap-2 pt-0.5">
            <button className="text-slate-400 hover:text-slate-700" onClick={() => openEdit(it._idx)} data-testid={`fe-edit-${it._idx}`}><Pencil className="h-4 w-4" /></button>
            <button className="text-slate-400 hover:text-red-600" onClick={() => deleteLine(it._idx)} data-testid={`fe-del-${it._idx}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        )}
        <span className={`min-w-[64px] text-right text-sm font-semibold ${unset ? "text-amber-600" : "text-slate-800"}`}>
          {unset ? "Set price" : fmtMoney(lineTotal(it))}
        </span>
      </div>
    );
  };

  return (
    <div>
      <BackLink id={id} />
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Edit quote (print layout)</h1>
        {saving && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
      </div>

      <div className="mx-auto max-w-3xl space-y-4">
        <Card className="p-5">
          <h2 className="text-lg font-bold text-slate-800">{quote.title || "Quotation"}</h2>
          {quote.customer_name && <p className="mt-0.5 text-sm text-slate-600">For: {quote.customer_name}</p>}
          <p className="mt-2 text-xs italic text-slate-400">This is exactly how the quote is grouped on the PDF — one total per door.</p>
        </Card>

        {!isLocked && unpricedIdxs.length > 0 && (
          <button onClick={openBulk} data-testid="fe-bulk-open" className="flex w-full items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-left text-sm font-semibold text-amber-700 hover:bg-amber-100">
            <Tags className="h-5 w-5" />
            <span className="flex-1">{unpricedIdxs.length} job{unpricedIdxs.length === 1 ? "" : "s"} need a price — set them all</span>
          </button>
        )}

        {doors.map((d, di) => {
          const note = (quote.door_notes || {})[d.key] || (quote.door_notes || {})[d.ref] || "";
          return (
            <Card key={d.key} className="overflow-hidden border-l-4 border-l-primary p-5" data-testid={`fe-door-${d.key}`}>
              <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-2">
                <div className="flex items-start gap-2">
                  {!isLocked && doors.length > 1 && (
                    <div className="flex flex-col">
                      <button disabled={di === 0} onClick={() => moveDoor(d.key, "up")} data-testid={`fe-door-up-${d.key}`} className="text-slate-400 enabled:hover:text-slate-700 disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
                      <button disabled={di === doors.length - 1} onClick={() => moveDoor(d.key, "down")} data-testid={`fe-door-down-${d.key}`} className="text-slate-400 enabled:hover:text-slate-700 disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
                    </div>
                  )}
                  <p className="font-bold text-slate-800">Door: {d.ref}{d.loc ? <span className="font-medium text-slate-500"> — {d.loc}</span> : null}</p>
                </div>
                <span className="text-base font-bold text-slate-900" data-testid={`fe-door-total-${d.key}`}>{fmtMoney(d.total)}</span>
              </div>

              {note ? (
                <button type="button" onClick={() => !isLocked && openNote(d.key, d.ref)} disabled={isLocked} data-testid={`fe-door-note-${d.key}`} className="mt-3 flex w-full items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-left">
                  <StickyNote className="h-4 w-4 shrink-0 text-amber-600" />
                  <span className="flex-1 text-xs italic text-amber-800">{note}</span>
                  {!isLocked && <Pencil className="h-3.5 w-3.5 text-amber-600" />}
                </button>
              ) : (
                !isLocked && (
                  <button type="button" onClick={() => openNote(d.key, d.ref)} data-testid={`fe-door-note-add-${d.key}`} className="mt-3 flex items-center gap-1.5 text-xs italic text-slate-400 hover:text-slate-600">
                    <Plus className="h-3.5 w-3.5" /> Add door note (e.g. access instructions)
                  </button>
                )
              )}

              <div className="mt-1 divide-y divide-slate-50">{d.items.map(renderLine)}</div>

              {!isLocked && (
                <div className="mt-3 flex flex-wrap gap-4 border-t border-slate-100 pt-3">
                  <button onClick={() => openAdd(d.key)} data-testid={`fe-add-to-door-${d.key}`} className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"><Plus className="h-4 w-4" /> Add job</button>
                  <button onClick={() => setApplyDoorKey(d.key)} data-testid={`fe-apply-tpl-${d.key}`} className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:underline"><Layers className="h-4 w-4" /> Apply template</button>
                  <button onClick={() => openSaveTpl(d.key)} data-testid={`fe-save-tpl-${d.key}`} className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:underline"><BookmarkPlus className="h-4 w-4" /> Save as template</button>
                </div>
              )}
            </Card>
          );
        })}

        {nonDoor.length > 0 && (
          <Card className="border-l-4 border-l-slate-300 p-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-2">
              <p className="font-bold text-slate-800">Additional Works</p>
              <span className="text-base font-bold text-slate-900">{fmtMoney(nonDoor.reduce((s, it) => s + lineTotal(it), 0))}</span>
            </div>
            <div className="mt-1 divide-y divide-slate-50">{nonDoor.map(renderLine)}</div>
            {!isLocked && (
              <div className="mt-3 border-t border-slate-100 pt-3">
                <button onClick={() => openAdd("")} data-testid="fe-add-additional" className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"><Plus className="h-4 w-4" /> Add job</button>
              </div>
            )}
          </Card>
        )}

        <Card className="p-5">
          <dl className="space-y-2 text-sm">
            <Row label="Subtotal" value={fmtMoney(quote.subtotal ?? liveSubtotal)} />
            {(quote.discount_amount || 0) > 0 && (
              <Row tone="emerald" label={`${quote.discount_label || "Discount"}${(Number(quote.discount_percent) || 0) > 0 ? ` (${Number(quote.discount_percent)}%)` : ""}`} value={`− ${fmtMoney(quote.discount_amount)}`} />
            )}
            {typeof quote.vat === "number" && <Row label="VAT" value={fmtMoney(quote.vat)} />}
            <div className="flex items-center justify-between border-t border-slate-100 pt-2">
              <dt className="font-bold text-slate-800">Total</dt>
              <dd className="text-lg font-bold text-primary" data-testid="fe-total">{fmtMoney(quote.total ?? liveSubtotal)}</dd>
            </div>
          </dl>
          {!isLocked && (
            <Button variant="outline" size="sm" className="mt-4 w-full" onClick={openDisc} data-testid="fe-discount-open">
              <PoundSterling className="mr-1.5 h-4 w-4" /> {(quote.discount_amount || 0) > 0 ? "Edit discount / goodwill" : "Add discount / goodwill"}
            </Button>
          )}
        </Card>

        {isLocked && <p className="pb-6 text-center text-sm text-slate-400">This quote is {quote.status} and can no longer be edited.</p>}
      </div>

      {/* Single-line edit */}
      <Dialog open={editIdx !== null} onOpenChange={(o) => !o && setEditIdx(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Edit job</DialogTitle><DialogDescription>Reword, reprice or change the quantity.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Job / description</Label><Textarea rows={2} value={eDesc} onChange={(e) => setEDesc(e.target.value)} data-testid="fe-edit-desc" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Price (£)</Label><Input type="number" step="0.01" min="0" value={eCost} onChange={(e) => setECost(e.target.value)} data-testid="fe-edit-cost" /></div>
              <div className="space-y-2"><Label>Quantity</Label><Input type="number" min="1" value={eQty} onChange={(e) => setEQty(e.target.value)} data-testid="fe-edit-qty" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={eSaveLib} onChange={(e) => setESaveLib(e.target.checked)} data-testid="fe-edit-save-lib" /> Save this price to my Price Log for future quotes</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditIdx(null)} disabled={saving}>Cancel</Button>
            <Button onClick={commitEdit} disabled={saving} data-testid="fe-edit-save">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add job */}
      <Dialog open={addDoorKey !== null} onOpenChange={(o) => !o && setAddDoorKey(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add job{addDoorKey ? ` to ${addDoorKey}` : ""}</DialogTitle><DialogDescription>Adds a chargeable line under this section.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Job name</Label><Input value={aName} onChange={(e) => setAName(e.target.value)} placeholder="e.g. Ease & adjust leaf" data-testid="fe-add-name" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Price (£)</Label><Input type="number" step="0.01" min="0" value={aCost} onChange={(e) => setACost(e.target.value)} data-testid="fe-add-cost" /></div>
              <div className="space-y-2"><Label>Quantity</Label><Input type="number" min="1" value={aQty} onChange={(e) => setAQty(e.target.value)} data-testid="fe-add-qty" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDoorKey(null)} disabled={saving}>Cancel</Button>
            <Button onClick={commitAdd} disabled={saving} data-testid="fe-add-save">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Add job</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk price */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>Set all prices</DialogTitle><DialogDescription>Every job still needing a price. Fill them in and save once.</DialogDescription></DialogHeader>
          <div className="space-y-1">
            {unpricedIdxs.length === 0 && <p className="py-4 text-center text-sm text-slate-500">Nothing to price — all jobs have a price. 🎉</p>}
            {unpricedIdxs.map(({ it, idx }) => (
              <div key={idx} className="flex items-center gap-3 border-b border-slate-50 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-700">{stripPrefix(it.description || it.component || "")}</p>
                  {(it.door_reference || it.door_location) && <p className="text-xs text-slate-400">{it.door_reference || ""}{it.door_location ? ` · ${it.door_location}` : ""}</p>}
                </div>
                <div className="flex w-28 items-center rounded-lg border border-slate-200 px-2">
                  <span className="text-sm font-semibold text-slate-500">£</span>
                  <Input type="number" step="0.01" min="0" className="border-0 px-1 focus-visible:ring-0" placeholder="0.00" value={bulkVals[idx] ?? ""} onChange={(e) => setBulkVals((p) => ({ ...p, [idx]: e.target.value }))} data-testid={`fe-bulk-price-${idx}`} />
                </div>
              </div>
            ))}
          </div>
          {unpricedIdxs.length > 0 && (
            <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={bulkSaveLib} onChange={(e) => setBulkSaveLib(e.target.checked)} data-testid="fe-bulk-save-lib" /> Save these to my Price Log for future quotes</label>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)} disabled={saving}>Close</Button>
            {unpricedIdxs.length > 0 && <Button onClick={commitBulk} disabled={saving} data-testid="fe-bulk-save">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save prices</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Door note */}
      <Dialog open={noteDoorKey !== null} onOpenChange={(o) => !o && setNoteDoorKey(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Door note</DialogTitle><DialogDescription>Prints under this door&apos;s heading on the quote.</DialogDescription></DialogHeader>
          <Textarea rows={3} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="e.g. Key held by concierge — 24h notice for access" data-testid="fe-door-note-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteDoorKey(null)} disabled={saving}>Cancel</Button>
            <Button onClick={saveNote} disabled={saving} data-testid="fe-door-note-save">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discount */}
      <Dialog open={discOpen} onOpenChange={setDiscOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Discount / goodwill</DialogTitle><DialogDescription>Applied to the subtotal before VAT. Set to 0 to remove.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Label (shown on quote)</Label><Input value={discLabel} onChange={(e) => setDiscLabel(e.target.value)} placeholder="e.g. Goodwill discount" data-testid="fe-disc-label" /></div>
            <div className="space-y-2">
              <Label>Type</Label>
              <div className="inline-flex rounded-lg border border-slate-200 p-0.5">
                {[{ v: "fixed", l: "Fixed £" }, { v: "percent", l: "Percent %" }].map((o) => (
                  <button key={o.v} type="button" onClick={() => setDiscMode(o.v)} data-testid={`fe-disc-mode-${o.v}`} className={`rounded-md px-3 py-1.5 text-xs font-medium ${discMode === o.v ? "bg-primary text-primary-foreground" : "text-slate-600 hover:bg-slate-100"}`}>{o.l}</button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>{discMode === "percent" ? "Percentage (%)" : "Amount (£)"}</Label>
              <Input type="number" step="0.01" min="0" value={discAmount} onChange={(e) => setDiscAmount(e.target.value)} placeholder={discMode === "percent" ? "e.g. 10" : "0.00"} data-testid="fe-disc-amount" />
              {discMode === "percent" && (Number(quote.subtotal) || 0) > 0 && (
                <p className="text-xs font-medium text-emerald-700">= − {fmtMoney((Number(quote.subtotal) || 0) * (parseFloat(discAmount) || 0) / 100)} off {fmtMoney(Number(quote.subtotal) || 0)}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={saveDisc} disabled={saving} data-testid="fe-disc-save">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply template */}
      <Dialog open={applyDoorKey !== null} onOpenChange={(o) => !o && setApplyDoorKey(null)}>
        <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-md">
          <DialogHeader><DialogTitle>Apply a template</DialogTitle><DialogDescription>Drops the template&apos;s jobs onto this door.</DialogDescription></DialogHeader>
          <div className="divide-y divide-slate-100">
            {templates.length === 0 && <p className="py-4 text-center text-sm text-slate-500">No templates yet. Use &quot;Save as template&quot; on a door to create one.</p>}
            {templates.map((t) => (
              <div key={t.id} className="flex items-center gap-3 py-3">
                <button onClick={() => applyTemplate(t)} data-testid={`fe-apply-tpl-row-${t.id}`} className="min-w-0 flex-1 text-left">
                  <p className="font-semibold text-slate-800">{t.name}</p>
                  <p className="text-xs text-slate-400">{(t.jobs || []).length} job{(t.jobs || []).length === 1 ? "" : "s"}</p>
                </button>
                <button onClick={() => deleteTemplate(t)} className="text-slate-400 hover:text-red-600" data-testid={`fe-del-tpl-${t.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setApplyDoorKey(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save as template */}
      <Dialog open={saveTplDoorKey !== null} onOpenChange={(o) => !o && setSaveTplDoorKey(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Save as template</DialogTitle><DialogDescription>Saves this door&apos;s jobs as a reusable set you can drop onto any door.</DialogDescription></DialogHeader>
          <div className="space-y-2"><Label>Template name</Label><Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="e.g. Standard communal remedial pack" data-testid="fe-tpl-name" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveTplDoorKey(null)} disabled={saving}>Cancel</Button>
            <Button onClick={commitSaveTpl} disabled={saving} data-testid="fe-tpl-save">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BackLink({ id }) {
  return (
    <Link to={`/quotes/${id}`} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
      <ArrowLeft className="h-4 w-4" /> Back to quote
    </Link>
  );
}

function Row({ label, value, tone }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-slate-500">{label}</dt>
      <dd className={`font-medium ${tone === "emerald" ? "text-emerald-600" : "text-slate-800"}`}>{value}</dd>
    </div>
  );
}
